import { AsyncState } from '@ts/async-state';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';

export class AsyncDataService<Data = unknown> {
  private state$ = new BehaviorSubject<AsyncState<Data>>({ status: 'idle' });

  readonly isIdle$: Observable<boolean>;

  readonly isLoading$: Observable<boolean>;

  readonly hasError$: Observable<boolean>;

  readonly hasData$: Observable<boolean>;

  readonly data$: Observable<Data>;

  readonly error$: Observable<any>;

  constructor() {
    this.isIdle$ = this.selectProperty('status', (status) => status === 'idle');
    this.isLoading$ = this.selectProperty(
      'status',
      (status) => status === 'loading'
    );
    this.hasError$ = this.selectProperty(
      'status',
      (status) => status === 'error'
    );
    this.hasData$ = this.selectProperty(
      'status',
      (status) => status === 'success'
    );
    this.data$ = this.selectProperty('data');
    this.error$ = this.selectProperty('error');
  }

  protected setIdle() {
    this.setState({
      status: 'idle',
    });
  }

  protected setLoading() {
    this.setState({
      status: 'loading',
    });
  }

  protected setData(data: Data) {
    this.setState({
      status: 'success',
      data,
    });
  }

  protected setError(error?: any) {
    this.setState({
      status: 'error',
      error,
    });
  }

  protected setState(newState: AsyncState<Data>) {
    this.state$.next({ ...newState });
  }

  private selectProperty<Key extends keyof AsyncState<Data>>(
    key: Key
  ): Observable<AsyncState<Data>[Key]>;
  private selectProperty<Key extends keyof AsyncState<Data>, R = any>(
    key: Key,
    mapFn?: (value: AsyncState<Data>[Key]) => R
  ): Observable<R>;
  private selectProperty(
    key: keyof AsyncState,
    mapFn: (value: any) => any = (value) => value
  ) {
    return this.state$.pipe(
      map((state) => mapFn(state[key])),
      distinctUntilChanged()
    );
  }
}
